import React, { Fragment } from 'react';
import { format as formatDate, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { keyBy, groupBy, sumBy, sortBy, isEmpty } from 'lodash';
import numeral from 'numeral';
import qs from 'qs';
import { Button } from 'reactstrap';
import fileDownload from 'js-file-download';
import { TextEncoder } from 'text-encoding';
import { toast } from 'react-toastify';

import firebase from '../../firebase';
import { conclusiveReferralFee } from '../../shared/models/order';
import AdminPage from '../hocs/AdminPage';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import QueryDateSelector from '../QueryDateSelector';
import TenantLink from '../TenantLink';
import ModalButton from '../ModalButton';
import ModelFormModal from '../modals/ModelFormModal';

const db = firebase.firestore();
const productsRef = db.collection('products');
const agentsRef = db.collection('agents');
const ordersRef = db.collection('orders');
const referrersRef = db.collectionGroup('referrers');

export default AdminPage(function AdminAgentReferralOrderPayments(props) {
  const { tenant, location, history } = props;
  const queryParams = useQueryParams();
  const { month = formatDate(addMonths(new Date(), -1), 'yyyy-MM') } = queryParams;
  const monthDate = endOfMonth((_ => new Date(_[0], _[1] - 1, 1))(month.split('-').map(_ => parseInt(_, 10))));
  const paymentDateString = formatDate(monthDate, 'yyyy-MM-dd');
  const paymentDateFormatted = paymentDateString.replace(/-/g, '/');

  const products = useCollectionSubscriptionInTenant(productsRef.orderBy('code'));
  const productsById = keyBy(products, 'id');
  const agents = useCollectionSubscriptionInTenant(agentsRef.orderBy('createdAt'));
  const agentsById = keyBy(agents, 'id');

  // paymentDateでordersを取得
  const orders = useCollectionSubscriptionInTenant(
    ordersRef
      .where('paymentDate', '>=', startOfMonth(monthDate))
      .where('paymentDate', '<=', endOfMonth(monthDate)),
    [month]
  );

  // referrerKeyからreferrersを取得
  const referrers = useCollectionsFetchInTenant(
    orders.filter(_ => _.referrerKey).map(_ => referrersRef.where('key', '==', _.referrerKey)),
    [orders.length]
  );
  const referrersById = keyBy(referrers, _ => _.key);

  // referrerの親の親がagentShop、その親の親がagent
  const computedOrders = orders.map((order) => {
    const { referrerKey } = order;
    const referrer = referrersById[referrerKey];
    const referralAgentId = referrer?.ref.parent.parent.parent.parent.id;
    return { ...order, referralAgentId, referrer };
  });

  // agentごとにグループ化
  const referralOrdersGroupedByAgentId = groupBy(
    computedOrders.filter(_ => _.referralAgentId && _.shippedDate),
    'referralAgentId'
  );

  // 各agentのpaymentAdjustmentsを取得
  const paymentAdjustmentsList = useCollectionsFetch(
    agents.map(_ =>
      _.ref
        .collection('paymentAdjustments')
        .where('paymentDate', '>=', startOfMonth(monthDate))
        .where('paymentDate', '<=', endOfMonth(monthDate))
    ),
    [agents.length, month]
  );
  const paymentAdjustmentsGroupedByAgentId = groupBy(
    paymentAdjustmentsList,
    _ => _.ref.parent.parent.id
  );

  // agent別の集計
  const rows = sortBy(
    agents
      .map((agent) => {
        const agentOrders = referralOrdersGroupedByAgentId[agent.id] || [];
        const uncancelledOrders = agentOrders.filter(_ => !_.cancelledAt);
        const orderReferralFee = sumBy(uncancelledOrders, _ => conclusiveReferralFee(productsById, _));

        // paymentAdjustmentsの合計を加算
        const paymentAdjustments = paymentAdjustmentsGroupedByAgentId[agent.id] || [];
        const adjustmentTotal = sumBy(paymentAdjustments, 'amount');
        const totalReferralFee = orderReferralFee + adjustmentTotal;

        return {
          agent,
          totalReferralFee,
        };
      })
      .filter(_ => _.totalReferralFee > 0),
    _ => -_.totalReferralFee
  );

  // 総合振込データ生成（固定長フォーマット120バイト）
  const generateBankTransferCSV = ({ transferDate }) => {
    // 不足データのチェック
    const missingDataAgents = rows.filter(_ =>
      !_.agent.bankCode || !_.agent.bankShopCode || !_.agent.bankAccountNumber || !_.agent.bankAccountNameKana
    );

    if (missingDataAgents.length > 0) {
      toast.error(`銀行情報が不足している代理店があります: ${missingDataAgents.map(_ => _.agent.name).join(', ')}`);
      return;
    }

    // 固定長フォーマット用のヘルパー関数
    // N = 数字（右詰め・0埋め）
    const padNum = (value, length) => String(value || '').padStart(length, '0');
    // C = 文字（左詰め・スペース埋め）
    const padChar = (value, length) => String(value || '').padEnd(length, ' ');

    const takumiDate = transferDate;
    const totalCount = rows.length;
    const totalAmount = sumBy(rows, 'totalReferralFee');

    // ① ヘッダーレコード（120バイト）
    const header = [
      '1', // 1. データ区分 N(1)
      '21', // 2. 種別コード N(2)
      ' ', // 3. コード区分 N(1) - スペース可
      padChar(tenant.bankTransferCommitteeCode || '', 10), // 4. 振込依頼人コード N(10) - スペース可
      padChar(tenant.bankTransferCommitteeName || '', 40), // 5. 振込依頼人名 C(40)
      padNum(takumiDate, 4), // 6. 取組日 N(4)
      '0005', // 7. 仕向銀行番号 N(4) - 三菱UFJ銀行
      padChar('ﾐﾂﾋﾞｼﾕ-ｴﾌｼﾞｴｲ', 15), // 8. 仕向銀行名 C(15)
      padNum(tenant.bankTransferBranchCode || '', 3), // 9. 仕向支店番号 N(3)
      padChar('', 15), // 10. 仕向支店名 C(15)
      tenant.bankTransferAccountType || '1', // 11. 預金種目 N(1) - 1:普通、2:当座
      padNum(tenant.bankTransferAccountNumber || '', 7), // 12. 口座番号 N(7)
      padChar('', 17), // 13. ダミー C(17)
    ].join('');

    // ② データレコード（120バイト）
    const dataRecords = rows.map(_ => [
      '2', // 1. データ区分 N(1)
      padNum(_.agent.bankCode || '', 4), // 2. 被仕向銀行番号 N(4)
      padChar('', 15), // 3. 被仕向銀行名 C(15)
      padNum(_.agent.bankShopCode || '', 3), // 4. 被仕向支店番号 N(3)
      padChar('', 15), // 5. 被仕向支店名 C(15)
      padChar('', 4), // 6. 手形交換所番号 N(4) - スペース
      _.agent.bankAccountType === 'checking' ? '2' : '1', // 7. 預金種目 N(1)
      padNum(_.agent.bankAccountNumber || '', 7), // 8. 口座番号 N(7)
      padChar(_.agent.bankAccountNameKana || '', 30), // 9. 受取人名 C(30)
      padNum(_.totalReferralFee, 10), // 10. 振込金額 N(10)
      '0', // 11. 新規コード N(1)
      padChar('', 10), // 12. 顧客コード1 C(10)
      padChar('', 10), // 13. 顧客コード2 C(10)
      ' ', // 14. 振込指定区分 N(1) - スペース可
      ' ', // 15. 識別表示 C(1) - スペース
      padChar('', 7), // 16. ダミー C(7)
    ].join(''));

    // ③ トレーラーレコード（120バイト）
    const trailer = [
      '8', // 1. データ区分 N(1)
      padNum(totalCount, 6), // 2. 合計件数 N(6)
      padNum(totalAmount, 12), // 3. 合計金額 N(12)
      padChar('', 101), // 4. ダミー C(101)
    ].join('');

    // ④ エンドレコード（120バイト）
    const end = [
      '9', // 1. データ区分 N(1)
      padChar('', 119), // 2. ダミー C(119)
    ].join('');

    // 全行を結合（CRLF改行）
    const fixedLengthData = [header, ...dataRecords, trailer, end].join('\r\n');

    // Shift_JISでエンコード
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const fileContent = encoder.encode(fixedLengthData);
    fileDownload(fileContent, `総合振込データ_${month}.txt`);
    toast.success('総合振込データをダウンロードしました');
  };

  return (
    <div className="container-fluid py-5">
      <div className="bg-white p-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>代理店紹介料支払一覧</h4>
        </div>

        <div className="d-flex align-items-end gap-1 mb-3">
          <QueryDateSelector
            label="年月"
            dateFormat={'yyyy年MM月末'}
            defaultValue={addMonths(new Date(), -1)}
            paramName="month"
            history={history}
            location={location}
            pickerProps={{ showMonthYearPicker: true, showFullMonthYearPicker: true }}
          />
        </div>

        <Fragment>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span>代理店数: {rows.length}</span>
              <span className="ml-3">
                紹介料合計: &yen; {numeral(sumBy(rows, 'totalReferralFee')).format()}
              </span>
            </div>
            <div>
              <ModalButton
                Modal={ModelFormModal}
                color="primary"
                disabled={rows.length === 0}
                modalProps={{
                  title: '総合振込データ出力',
                  submitLabel: '出力',
                  fields: {
                    transferDate: {
                      label: '振込日（MMdd形式）',
                      type: 'string',
                      initialValue: formatDate(monthDate, 'MMdd'),
                      validations: {
                        required: v => !isEmpty(v),
                        format: v => /^\d{4}$/.test(v),
                      },
                    },
                  },
                  onSubmit: async (values, { onClickClose }) => {
                    generateBankTransferCSV(values);
                    onClickClose();
                  },
                }}
              >
                総合振込データCSV
              </ModalButton>
            </div>
          </div>

          <div className="overflow-auto">
            {rows.length > 0 ? (
              <table className="table table-bordered">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 200 }}>代理店名</th>
                    <th style={{ minWidth: 150 }}>支払金額(税込)</th>
                    <th style={{ minWidth: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ agent, totalReferralFee, }) => {

                    return (
                      <tr key={agent.id}>
                        <td>{agent.name}</td>
                        <td className="text-right">{numeral(totalReferralFee).format('0,0')}</td>
                        <td className="text-center">
                          <TenantLink to={`/agents/${agent.id}/referralOrders?${qs.stringify({ dateType: ['paymentDate'], month, showsPaymentStatementModal: 1 })}`} target="_blank">
                            詳細
                            <span className="fas fa-external-link-alt ml-1" />
                          </TenantLink>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div>該当する代理店がありません</div>
            )}
          </div>
        </Fragment>
      </div>
    </div>
  );
});
